import { logger } from '../utils/logger';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
} from '@mui/material';
import { tokenService, TOKEN_INFO_EVENT, TokenInfo } from '../services/tokenService';
import { useAuth } from '../contexts/AuthContext';

const WARNING_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

const formatDuration = (ms: number) => {
  const clamped = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(clamped / 60);
  const seconds = clamped % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const SessionTimeoutManager: React.FC = () => {
  const { isAuthenticated, logoutAndRedirect } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const expiryRef = useRef<number | null>(null);

  const clearWarningTimer = () => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  };

  const clearCountdown = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };

  const hideWarning = useCallback(() => {
    setShowWarning(false);
    clearCountdown();
    expiryRef.current = null;
  }, []);

  const triggerWarning = useCallback((expiresAt: number) => {
    expiryRef.current = expiresAt;
    setShowWarning(true);
    setTimeRemaining(Math.max(0, expiresAt - Date.now()));

    clearCountdown();
    tokenService.pauseAutoRefresh();

    countdownTimerRef.current = setInterval(() => {
      if (!expiryRef.current) {
        return;
      }
      setTimeRemaining(Math.max(0, expiryRef.current - Date.now()));
    }, 1000);
  }, []);

  const scheduleWarning = useCallback(
    (tokenInfo: TokenInfo | null) => {
      clearWarningTimer();
      if (!tokenInfo || !isAuthenticated) {
        hideWarning();
        return;
      }

      const delay = tokenInfo.expiresAt - Date.now() - WARNING_BUFFER_MS;
      if (delay <= 0) {
        triggerWarning(tokenInfo.expiresAt);
        return;
      }

      warningTimerRef.current = setTimeout(() => {
        triggerWarning(tokenInfo.expiresAt);
      }, delay);
    },
    [hideWarning, isAuthenticated, triggerWarning],
  );

  const handleTokenInfoEvent = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<TokenInfo | null>;
      scheduleWarning(customEvent.detail ?? null);
    },
    [scheduleWarning],
  );

  const handleStayLoggedIn = async () => {
    setIsRefreshing(true);
    try {
      await tokenService.refreshToken();
      tokenService.resumeAutoRefresh();
      const info = tokenService.getTokenInfo();
      hideWarning();
      scheduleWarning(info);
    } catch (error) {
      logger.error('[SessionTimeout] Refresh failed, logging out', error);
      logoutAndRedirect();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = () => {
    hideWarning();
    tokenService.removeToken();
    logoutAndRedirect();
  };

  useEffect(() => {
    if (!isAuthenticated) {
      hideWarning();
      clearWarningTimer();
      clearCountdown();
      return;
    }

    scheduleWarning(tokenService.getTokenInfo());
  }, [hideWarning, isAuthenticated, scheduleWarning]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.addEventListener(TOKEN_INFO_EVENT, handleTokenInfoEvent as EventListener);
    return () => {
      window.removeEventListener(TOKEN_INFO_EVENT, handleTokenInfoEvent as EventListener);
      clearWarningTimer();
      clearCountdown();
    };
  }, [handleTokenInfoEvent]);

  if (!showWarning) {
    return null;
  }

  const progress =
    expiryRef.current && WARNING_BUFFER_MS > 0
      ? Math.min(100, Math.max(0, (timeRemaining / WARNING_BUFFER_MS) * 100))
      : 0;

  return (
    <Dialog open={showWarning} maxWidth="xs" fullWidth>
      <DialogTitle>Stay Signed In?</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Your session is about to expire for security reasons. Continue to stay logged in or log out now.
        </Typography>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Time remaining
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDuration(timeRemaining)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 999, mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary">
          We’ll save your work before logging out.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleLogout}>Log out</Button>
        <Button
          variant="contained"
          onClick={handleStayLoggedIn}
          disabled={isRefreshing}
        >
          {isRefreshing ? 'Refreshing…' : 'Continue session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutManager;
